import React from 'react';
import { trackPageEmployeesChange } from '@xingternal/pages-tracking';
import { Carousel, getImageGender } from '@xingternal/pages-common';
import {
  EmployeesCardVerticalContainer,
  EmployeeCardFencing,
} from '../EmployeesCardVertical';
import styles from '../EmployeesContainer.css';
import {
  NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_IN,
  NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_OUT,
} from '../config';
import {
  Employee,
  EmployeeNode,
  NetworkRequestResponseUpdateCache,
} from '../../types';

type Props = {
  employeesList: EmployeeNode[];
  totalEmployees: number;
  authenticated: boolean;
  currentUserId: string;
  updateEmployeesCache: NetworkRequestResponseUpdateCache;
};

const getEmployeesCards = (
  employee: Employee,
  {
    authenticated,
    currentUserId,
  }: { authenticated?: boolean; currentUserId?: string },
  updateEmployeesCache: NetworkRequestResponseUpdateCache
) => {
  const { profileDetails, networkRelationship, contactDistance, hasPhoto } =
    employee;

  const {
    id,
    profileImage,
    gender,
    displayName,
    pageName,
    clickReasonProfileUrl,
    occupations,
    userFlags,
  } = profileDetails;
  const ocupationTitle = occupations?.[0]?.subline || '';
  return (
    <EmployeesCardVerticalContainer
      key={id}
      id={id}
      profileImage={{
        src: profileImage?.[0]?.url,
        type: getImageGender(gender),
        profileName: displayName,
        alt: `${displayName}'s profile picture`,
        connectionDegree: contactDistance?.distance,
      }}
      pageName={pageName}
      profileUrl={clickReasonProfileUrl?.profileUrl || `/profile/${pageName}`}
      profileName={displayName}
      position={ocupationTitle}
      relationship={networkRelationship?.relationship}
      authenticated={authenticated}
      isCurrentUser={currentUserId === id}
      flag={userFlags?.displayFlag}
      hasPhoto={hasPhoto}
      updateEmployeesCache={updateEmployeesCache}
    />
  );
};

const filterEmployeesList = ({
  employeesList = [],
  numberOfEmployees = 10,
}: {
  employeesList: EmployeeNode[];
  numberOfEmployees?: number;
}): Employee[] =>
  employeesList
    .filter(({ node }) => node && node.profileDetails !== null)
    .slice(0, numberOfEmployees)
    .map(({ node }) => node);

const EmployeesCarousel = ({
  employeesList,
  totalEmployees,
  authenticated,
  currentUserId,
  updateEmployeesCache,
}: Props) => {
  const employeesListFiltered = filterEmployeesList({
    employeesList,
    numberOfEmployees: authenticated
      ? NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_IN
      : NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_OUT,
  });

  const showFencing =
    !authenticated && totalEmployees > NUMBER_OF_EMPLOYEES_TO_SHOW_LOGGED_OUT;

  return (
    <Carousel
      skin={{ carousel: styles.carousel, carouselMain: styles.carouselMain }}
      onPageChange={trackPageEmployeesChange}
    >
      {employeesListFiltered.map((employee) =>
        getEmployeesCards(
          employee,
          { authenticated, currentUserId },
          updateEmployeesCache
        )
      )}
      {showFencing ? (
        <EmployeeCardFencing employeesNumber={totalEmployees} />
      ) : (
        React.Fragment
      )}
    </Carousel>
  );
};

export default EmployeesCarousel;
